import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useDriverStateV2 } from '../../hooks/useDriverStateV2';
import { getStoredSelectedCompanyId } from '../../hooks/userCompanies.shared';
import type { DriverSortBy, ListParams } from '@relay-towing/api';
import { getList } from '../list';

export interface CompanyDriver {
  id: string;
  name: string;
  truckNumber: string | null;
  locationLatitude: number;
  locationLongitude: number;
  status: 'ACTIVE' | 'CLEAR' | 'DISPATCHED' | 'ON_LEAVE';
  email: string;
  phone: string | null;
  user?: { id: string };
}

export const useGetAllDrivers = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  // An office member with no Driver row has no driver company; the stored
  // selection stands in, as it does for creating a call.
  const companyId = driver?.companyId || getStoredSelectedCompanyId();

  return useQuery({
    queryKey: ['all-drivers', companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();

      // The old request sent `page=1`, which /drivers never read; pageNo is
      // the name it pages by, and 1 is its default either way.
      // Whoever drives, in the server's order.
      const params = {
        pageNo: 1,
        pageSize: 1000,
      } satisfies ListParams<DriverSortBy>;
      const { data: drivers } = await getList<CompanyDriver>(
        '/drivers',
        params,
        { token, companyId }
      );
      return drivers;
    },
    enabled: !!companyId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every 60 seconds for location updates
  });
};
